import React, { useState } from 'react';
import { Button, List, Typography, Tag, Spin } from 'antd';
import {
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
// Types
import { GetCbTaskExtensionHistory_getAgencyCbTaskExtensionHistory } from 'api/campaignV2/types/GetCbTaskExtensionHistory';
import { TaskExtensionRequestStatus } from 'api/graphql-global-types';
// Styles
import styles from './ExtensionHistory.module.scss';

const { Text } = Typography;

type ExtensionHistoryProps = {
  deliverableId: string;
  offerId: string;
  getCbTaskExtensionHistory: (options: any) => void;
  extensionHistoryData?: GetCbTaskExtensionHistory_getAgencyCbTaskExtensionHistory[];
  extensionHistoryLoading?: boolean;
};

const ExtensionHistory: React.FC<ExtensionHistoryProps> = ({
  deliverableId,
  offerId,
  getCbTaskExtensionHistory,
  extensionHistoryData = [],
  extensionHistoryLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleHistory = () => {
    if (!isExpanded && extensionHistoryData.length === 0) {
      // Fetch history when expanding for the first time
      getCbTaskExtensionHistory({
        variables: {
          input: {
            deliverableId,
            offerId,
          },
        },
      });
    }
    setIsExpanded(!isExpanded);
  };

  const getStatusIcon = (status: TaskExtensionRequestStatus) => {
    switch (status) {
      case TaskExtensionRequestStatus.APPROVED:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case TaskExtensionRequestStatus.REJECTED:
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case TaskExtensionRequestStatus.PENDING:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusTag = (status: TaskExtensionRequestStatus) => {
    switch (status) {
      case TaskExtensionRequestStatus.APPROVED:
        return <Tag color="green">Approved</Tag>;
      case TaskExtensionRequestStatus.REJECTED:
        return <Tag color="red">Rejected</Tag>;
      case TaskExtensionRequestStatus.PENDING:
        return <Tag color="orange">Pending</Tag>;
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  const formatDate = (date: string) => {
    return dayjs(date).format('MMM D, YYYY [at] h:mm A');
  };

  return (
    <div className={styles.root}>
      <Button
        type="link"
        icon={<HistoryOutlined />}
        onClick={handleToggleHistory}
        className={styles.toggleButton}
      >
        View Extension History
      </Button>

      {isExpanded && (
        <div className={styles.historyContainer}>
          {extensionHistoryLoading ? (
            <div className={styles.loadingContainer}>
              <Spin size="small" />
              <Text type="secondary">Loading history...</Text>
            </div>
          ) : extensionHistoryData.length === 0 ? (
            <div className={styles.emptyContainer}>
              <Text type="secondary">No extension history found</Text>
            </div>
          ) : (
            <List
              size="small"
              dataSource={extensionHistoryData}
              renderItem={(item) => (
                <List.Item className={styles.historyItem}>
                  <div className={styles.historyContent}>
                    <div className={styles.historyHeader}>
                      <div className={styles.statusInfo}>
                        {getStatusIcon(item.status)}
                        {getStatusTag(item.status)}
                      </div>
                      <Text type="secondary" className={styles.dateText}>
                        {formatDate(item.createdAt)}
                      </Text>
                    </div>

                    <div className={styles.historyDetails}>
                      <Text strong>Requested:</Text>
                      <Text>
                        {item.requestedExtensionDays}{' '}
                        {item.requestedExtensionDays === 1 ? 'day' : 'days'}
                      </Text>
                    </div>

                    <div className={styles.historyDetails}>
                      <Text strong>Reason:</Text>
                      <Text>{item.reason}</Text>
                    </div>

                    {item.reviewedBy && (
                      <div className={styles.historyDetails}>
                        <Text strong>Reviewed by:</Text>
                        <Text>
                          {item.reviewedBy.firstName} {item.reviewedBy.lastName}
                        </Text>
                      </div>
                    )}

                    {item.updatedAt !== item.createdAt && (
                      <div className={styles.historyDetails}>
                        <Text strong>Updated:</Text>
                        <Text>{formatDate(item.updatedAt)}</Text>
                      </div>
                    )}
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ExtensionHistory;
